// Copyright (c) 2026 GLOBAL MICRO IT TECHNOLOGY SOLUTIONS LIMITED
// Company No. 758762, Dublin, Ireland
// All rights reserved. Proprietary and confidential.

import { useState, useEffect, useCallback, useRef } from 'react';
import { parseNavHash } from '../utils/navigation';
import type { ActiveTab } from '../types/navigation';

export interface HashNavState {
  /** Tab parsed from URL hash (null if absent or invalid) */
  tab: ActiveTab | null;
  /** Query params parsed from URL hash */
  params: Record<string, string>;
  /** Strip params from hash after consuming — prevents re-filtering on refresh */
  clearParams: () => void;
}

// Per-agent tab set — Shared-CSS-Reference v3.1 §2.2 (VALID_TABS / ActiveTab are per-agent).
const VALID_TABS = new Set<string>([
  'dashboard', 'projects', 'chat', 'registers', 'estate', 'migrate', 'lab', 'settings',
  'project-detail', 'document-viewer', 'assessment-chat',
  'cost-estimate', 'audit-navigator', 'customer-settings', 'questionnaire',
]);

/**
 * Reads the URL hash on mount and on hashchange events.
 * Returns the parsed tab + params, and a clearParams function
 * that strips the query string (keeping just the tab name).
 */
export function useHashNavigation(): HashNavState {
  const [state, setState] = useState<{ tab: ActiveTab | null; params: Record<string, string> }>(() => {
    const parsed = parseNavHash(window.location.hash);
    if (parsed && VALID_TABS.has(parsed.tab)) {
      return { tab: parsed.tab as ActiveTab, params: parsed.params };
    }
    return { tab: null, params: {} };
  });

  const consumed = useRef(false);

  useEffect(() => {
    const handler = () => {
      const parsed = parseNavHash(window.location.hash);
      if (parsed && VALID_TABS.has(parsed.tab)) {
        consumed.current = false;
        setState({ tab: parsed.tab as ActiveTab, params: parsed.params });
      }
    };
    window.addEventListener('hashchange', handler);
    return () => window.removeEventListener('hashchange', handler);
  }, []);

  const clearParams = useCallback(() => {
    if (consumed.current) return;
    consumed.current = true;
    if (state.tab) {
      window.history.replaceState(null, '', `${window.location.pathname}#${state.tab}`);
      setState(prev => ({ ...prev, params: {} }));
    }
  }, [state.tab]);

  return { tab: state.tab, params: state.params, clearParams };
}
